import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { PlayerCoachManualEditModal } from "../dashboard/PlayerCoachManualEditModal";
import { fetchUsersByRoles, setManualEditsFilters, updateUserProfile } from "../../../features/admin/adminSlice";
import { openOnRowClick } from '../../../utils/rowClick';

const ManualEditsTable = ({ roleFilter, searchQuery = "", teamFilter = "" }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { usersByRole, manualEditsLoading, manualEditsError, manualEditsFilters, manualEditsPagination } = useSelector(
    (state) => state.admin
  );

  // Single effect that handles all filter changes together - avoids race conditions
  useEffect(() => {
    dispatch(setManualEditsFilters({
      page: 1,
      role: roleFilter || 'player',
      search: searchQuery,
      teamId: teamFilter || undefined,
    }));
  }, [dispatch, roleFilter, searchQuery, teamFilter]);

  useEffect(() => {
    dispatch(fetchUsersByRoles(manualEditsFilters));
  }, [dispatch, manualEditsFilters]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);

  const handleEdit = (user) => {
    if (user) {
      setSelectedProfile(user);
      setIsModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedProfile(null);
  };

  const handleSaveChanges = async (formData) => {
    if (!selectedProfile?._id) return;
    try {
      await dispatch(updateUserProfile({ id: selectedProfile._id, profileData: formData })).unwrap();
      handleCloseModal();
      // Refresh the list
      dispatch(setManualEditsFilters({ page: 1 }));
    } catch (error) {
      console.error("Failed to save changes:", error);
    }
  };

  const getModalMode = () =>
    !selectedProfile ? "player" : selectedProfile.role.toLowerCase();

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatCommitmentStatus = (user) => {
    if (user.commitmentStatus === 'committed' && user.committedTo) {
      return `Committed "${user.committedTo}"`;
    }
    return user.commitmentStatus === 'committed' ? 'Committed' : 'Uncommitted';
  };

  const formatName = (user) => {
    const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
    return fullName || 'N/A';
  };

  const formatTeam = (user) => {
    if (user.team && typeof user.team === 'object' && user.team.name) {
      return user.team.name;
    }
    if (user.school) return user.school;
    return '-';
  };

  const handleLoadMore = () => {
    const nextPage = manualEditsPagination.currentPage + 1;
    dispatch(setManualEditsFilters({ page: nextPage }));
  };

  const handleViewProfile = (user) => {
    const role = user.role.toLowerCase();
    if (role === 'player') {
      navigate(`/admin/player/${user._id}`);
    } else if (role === 'coach') {
      navigate(`/admin/coach/${user._id}`);
    } else if (role === 'scout') {
      navigate(`/admin/scout/${user._id}`);
    }
  };

  if (manualEditsError) {
    return (
      <div className="rounded-3xl bg-jp-card border border-jp-line p-6">
        <p className="text-red-600 dark:text-red-400 text-center">{manualEditsError}</p>
      </div>
    );
  }

  return (
    <div className="rounded-3xl bg-jp-card border border-jp-line overflow-hidden flex flex-col" style={{ height: '400px' }}>
      <div className="overflow-y-auto flex-1">
        <table className="w-full border-collapse">
          <thead className="bg-jp-card sticky top-0 z-10">
            <tr className="border-b border-jp-line">
              <th className="text-center px-4 py-3 text-[11px] font-medium text-jp-muted uppercase bg-jp-card">
                NAME
              </th>
              <th className="text-center px-4 py-3 text-[11px] font-medium text-jp-muted uppercase bg-jp-card">
                TEAM / SCHOOL
              </th>
              <th className="text-center px-4 py-3 text-[11px] font-medium text-jp-muted uppercase bg-jp-card">
                ROLE
              </th>
              <th className="text-center px-4 py-3 text-[11px] font-medium text-jp-muted uppercase bg-jp-card">
                LAST UPDATE
              </th>
              <th className="text-center px-4 py-3 text-[11px] font-medium text-jp-muted uppercase bg-jp-card">
                STATUS
              </th>
              {roleFilter !== 'coach' && roleFilter !== 'scout' && (
                <th className="text-center px-4 py-3 text-[11px] font-medium text-jp-muted uppercase bg-jp-card">
                  COMMITMENT STATUS
                </th>
              )}
              <th className="text-center px-4 py-3 text-[11px] font-medium text-jp-muted uppercase bg-jp-card">
                ACTIONS
              </th>
            </tr>
          </thead>
          <tbody>
            {manualEditsLoading && usersByRole.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-jp-muted">
                  Loading...
                </td>
              </tr>
            ) : usersByRole.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-jp-muted">
                  No users found
                </td>
              </tr>
            ) : (
              usersByRole.map((user) => (
                <tr
                  key={user._id}
                  // Only player rows open a profile, matching the View Profile button
                  onClick={user.role?.toLowerCase() === "player" ? openOnRowClick(() => handleViewProfile(user)) : undefined}
                  className={`border-t border-jp-line hover:bg-jp-section h-12 ${user.role?.toLowerCase() === "player" ? "cursor-pointer" : ""}`}
                >
                  <td className="px-4 text-xs text-jp-ink align-middle text-center">
                    {formatName(user)}
                  </td>
                  <td className="px-4 text-xs text-jp-ink align-middle text-center">
                    {formatTeam(user)}
                  </td>
                  <td className="px-4 text-xs text-jp-ink align-middle text-center">
                    {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                  </td>
                  <td className="px-4 text-xs text-jp-ink align-middle text-center">
                    {formatDate(user.updatedAt)}
                  </td>
                  <td className="px-4 text-xs text-jp-ink align-middle text-center">
                    {user.registrationStatus.charAt(0).toUpperCase() + user.registrationStatus.slice(1)}
                  </td>
                  {roleFilter !== 'coach' && roleFilter !== 'scout' && (
                    <td className="px-4 text-xs text-jp-ink align-middle text-center">
                      {formatCommitmentStatus(user)}
                    </td>
                  )}
                  <td className="px-4 text-xs align-middle text-center">
                    {(() => {
                      const isPlayer = user.role?.toLowerCase() === "player";
                      return (
                        <div className="relative group flex items-center gap-2 justify-center">
                          <button
                            onClick={() => isPlayer && handleViewProfile(user)}
                            disabled={!isPlayer}
                            className={`text-[11px] transition-colors ${isPlayer ? "text-sky-600 dark:text-sky-400 hover:text-jp-accent cursor-pointer" : "text-jp-muted cursor-not-allowed"}`}
                          >
                            View Profile
                          </button>
                          <span className="text-jp-muted/70">|</span>
                          <button
                            onClick={() => isPlayer && handleEdit(user)}
                            disabled={!isPlayer}
                            className={`text-[11px] flex items-center gap-1 ${isPlayer ? "text-jp-ink hover:text-jp-accent cursor-pointer" : "text-jp-muted cursor-not-allowed"}`}
                          >
                            Edit <span>✏️</span>
                          </button>
                          {!isPlayer && (
                            <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-jp-section text-jp-ink text-[10px] px-2 py-1 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-20">
                              Only works on player role
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Load More Button */}
      {manualEditsPagination.hasMore && (
        <div className="px-6 py-4 border-t border-jp-line flex justify-center">
          <button
            onClick={handleLoadMore}
            disabled={manualEditsLoading}
            className="px-6 py-2 rounded-lg bg-sky-600 text-white text-sm font-medium hover:bg-sky-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {manualEditsLoading ? (
              <>
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Loading...
              </>
            ) : (
              <>
                Load More
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </>
            )}
          </button>
        </div>
      )}

      {/* Showing count */}
      <div className="px-6 py-3 border-t border-jp-line text-center">
        <p className="text-xs text-jp-muted">
          Showing {usersByRole.length} of {manualEditsPagination.totalCount} users
        </p>
      </div>

      <PlayerCoachManualEditModal
        open={isModalOpen}
        mode={getModalMode()}
        onClose={handleCloseModal}
        onSave={handleSaveChanges}
        userData={selectedProfile}
      />
    </div>
  );
};

export default ManualEditsTable;
